import axios from 'axios';
import authService from './authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const BASE_PATH = '/api/v1/hot-props';

const getAuthHeaders = () => {
    const token = authService.getToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

const handleApiError = (error) => {
    if (error.response) {
        const { status, data } = error.response;

        if (status === 401) {
            return {
                success: false,
                status,
                errorCode: data.error || 'UNAUTHORIZED',
                message: data.message || 'Authentication failed',
                isAuthError: true
            };
        }

        if (status === 403) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SUBSCRIPTION_REQUIRED',
                message: data.message || 'A Snipe Pro or Snipe Elite subscription is required to access hot props data. Please upgrade your plan.',
                isSubscriptionError: true
            };
        }

        if (status === 400) {
            return {
                success: false,
                status,
                errorCode: data.error || 'VALIDATION_ERROR',
                message: data.message || 'Invalid request parameters',
                details: data.errors || []
            };
        }

        if (status === 429) {
            return {
                success: false,
                status,
                errorCode: data.error || 'RATE_LIMIT_EXCEEDED',
                message: data.message || 'Too many hot props data requests. Please try again in a minute.'
            };
        }

        if (status === 503) {
            const code = data.error || 'SERVICE_UNAVAILABLE';
            const isNoData = code === 'NO_DATA';
            return {
                success: false,
                status,
                errorCode: code,
                message: data.message || (isNoData
                    ? 'No hot props data is currently available. Data may be updating.'
                    : 'Hot props service is temporarily unavailable. Please try again later.'),
                isNoData
            };
        }

        if (status === 500) {
            return {
                success: false,
                status,
                errorCode: data.error || 'SERVER_ERROR',
                message: data.message || 'Server error. Please try again later.'
            };
        }

        return {
            success: false,
            status,
            errorCode: data.error || 'SERVER_ERROR',
            message: data.message || 'Server error. Please try again later.'
        };
    }

    return {
        success: false,
        status: null,
        errorCode: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.'
    };
};

const validateHotPropsPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return false;
    if (!Array.isArray(payload.props)) return false;
    return true;
};

const buildQueryParams = (params = {}) => {
    const {
        sport = 'afl',
        filter_type,
        movement_threshold,
        edge_threshold,
        movement_bookmaker,
        exclude_best_bookmakers,
        minimum_odds,
    } = params;

    const query = { sport };

    if (filter_type === 'market_movers' || filter_type === 'most_edge') {
        query.filter_type = filter_type;
    }

    // movement_threshold is accepted by BOTH filter types (different defaults, same param name).
    if (movement_threshold != null && movement_threshold !== '') {
        query.movement_threshold = movement_threshold;
    }

    if (filter_type === 'most_edge') {
        if (edge_threshold != null && edge_threshold !== '') {
            query.edge_threshold = edge_threshold;
        }
        if (typeof movement_bookmaker === 'string' && movement_bookmaker.trim()) {
            query.movement_bookmaker = movement_bookmaker.trim().toLowerCase();
        }
        if (Array.isArray(exclude_best_bookmakers) && exclude_best_bookmakers.length > 0) {
            query.exclude_best_bookmakers = exclude_best_bookmakers
                .map((b) => (typeof b === 'string' ? b.trim().toLowerCase() : ''))
                .filter(Boolean)
                .join(',');
        }
        if (minimum_odds != null && minimum_odds !== '' && !Number.isNaN(Number(minimum_odds))) {
            query.minimum_odds = Number(minimum_odds);
        }
    }

    return query;
};

const hotPropsService = {
    getHotProps: async (params = {}) => {
        // Backward-compat: callers that pass a plain string are treated as sport
        const normalizedParams =
            typeof params === 'string' ? { sport: params } : (params || {});

        try {
            const response = await axios.get(`${API_URL}${BASE_PATH}`, {
                params: buildQueryParams(normalizedParams),
                headers: getAuthHeaders()
            });

            const result = response.data;

            if (!result || result.success === false) {
                return {
                    success: false,
                    message: result?.message || 'Failed to fetch hot props data'
                };
            }

            if (!validateHotPropsPayload(result.data)) {
                return {
                    success: false,
                    errorCode: 'INVALID_RESPONSE',
                    message: 'Hot props response was malformed.'
                };
            }

            return {
                success: true,
                data: result.data
            };
        } catch (error) {
            return handleApiError(error);
        }
    }
};

export default hotPropsService;
